import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { getConversations, getMessages, sendMessage } from "../lib/api";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { MessageSquare, Send, ArrowLeft } from "lucide-react";

export default function MessagingPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await getConversations();
        setConversations(res.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!activeConv) return;
    const fetchMessages = async () => {
      try {
        const res = await getMessages(activeConv.conversation_id);
        setMessages(res.data);
      } catch (err) { console.error(err); }
    };
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || !activeConv) return;
    setSending(true);
    try {
      await sendMessage(activeConv.conversation_id, newMsg);
      setNewMsg("");
      const res = await getMessages(activeConv.conversation_id);
      setMessages(res.data);
    } catch (err) { console.error(err); }
    setSending(false);
  };

  const getOtherName = (conv) => {
    if (!conv.participant_names) return "Unknown";
    const other = conv.participants?.find(p => p !== user?.user_id);
    return conv.participant_names[other] || "Unknown";
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <h1 className="text-2xl font-bold text-[#0d0d0d] font-['Fraunces'] mb-6" data-testid="messages-heading">Messages</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-220px)]">
          {/* Conversations List */}
          <div className="bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden flex flex-col">
            <div className="p-4 border-b border-[#d5d3ce]">
              <p className="font-semibold text-sm text-[#0d0d0d]">Conversations</p>
            </div>
            <div className="flex-1 overflow-y-auto" data-testid="conversations-list">
              {conversations.length === 0 ? (
                <div className="p-6 text-center">
                  <MessageSquare className="w-8 h-8 text-[#d5d3ce] mx-auto mb-2" />
                  <p className="text-sm text-[#5a5a5a]">No conversations yet</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <button
                    key={conv.conversation_id}
                    onClick={() => setActiveConv(conv)}
                    data-testid={`conv-${conv.conversation_id}`}
                    className={`w-full text-left p-4 border-b border-[#e5e3de] hover:bg-[#faf8f3] transition-colors ${
                      activeConv?.conversation_id === conv.conversation_id ? "bg-[#faf8f3]" : ""
                    }`}
                  >
                    <p className="font-semibold text-[#0d0d0d] text-sm truncate">{getOtherName(conv)}</p>
                    <p className="text-xs text-[#5a5a5a] truncate mt-0.5">{conv.last_message}</p>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Messages */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-[#d5d3ce]/60 overflow-hidden flex flex-col">
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 text-[#d5d3ce] mx-auto mb-3" />
                  <p className="text-[#5a5a5a]">Select a conversation</p>
                </div>
              </div>
            ) : (
              <>
                <div className="p-4 border-b border-[#d5d3ce] flex items-center gap-3">
                  <button onClick={() => setActiveConv(null)} className="md:hidden">
                    <ArrowLeft className="w-5 h-5 text-[#5a5a5a]" />
                  </button>
                  <p className="font-semibold text-[#0d0d0d]">{getOtherName(activeConv)}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-list">
                  {messages.map(msg => (
                    <div key={msg.message_id} className={`flex ${msg.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                        msg.sender_id === user?.user_id
                          ? "bg-[#1a6b3c] text-white rounded-br-md"
                          : "bg-[#faf8f3] text-[#0d0d0d] rounded-bl-md"
                      }`}>
                        <p className="text-sm">{msg.text}</p>
                        <p className={`text-[10px] mt-1 ${msg.sender_id === user?.user_id ? "text-white/60" : "text-[#5a5a5a]"}`}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
                <form onSubmit={handleSend} className="p-4 border-t border-[#d5d3ce] flex gap-2">
                  <Input
                    data-testid="message-input"
                    value={newMsg}
                    onChange={(e) => setNewMsg(e.target.value)}
                    placeholder="Type a message..."
                    className="rounded-xl border-[#d5d3ce]"
                    autoComplete="off"
                  />
                  <Button type="submit" disabled={sending || !newMsg.trim()} data-testid="send-msg-btn" className="rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white px-4">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
